import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { X, AlertCircle, Check } from 'lucide-react';

interface CheckoutNoteDialogProps {
  open: boolean;
  onClose: () => void;
  note: string;
}

export function CheckoutNoteDialog({ open, onClose, note }: CheckoutNoteDialogProps) {
  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        </Transition.Child>

        {/* Centered zoom-in modal */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95 translate-y-4"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100 translate-y-0"
              leaveTo="opacity-0 scale-95 translate-y-4"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-2xl border border-brand-100 flex flex-col max-h-[90vh]">
                {/* Header - brand theme */}
                <div className="bg-gradient-to-r from-brand-50 to-brand-100/80 px-6 py-5 border-b border-brand-200">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-brand-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <AlertCircle className="h-5 w-5 text-brand-600" />
                      </div>
                      <Dialog.Title className="text-xl font-bold text-gray-900">
                        Important Notice
                      </Dialog.Title>
                    </div>
                    <button
                      onClick={onClose}
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-white/60 rounded-full transition-colors"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Content - scrollable */}
                <div className="flex-1 overflow-y-auto p-6">
                  <div className="bg-brand-50/50 rounded-2xl p-5 border border-brand-100 text-left">
                    <div className="text-gray-700 leading-relaxed whitespace-pre-wrap text-sm sm:text-base">
                      {note}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="border-t border-brand-100 px-6 py-5 bg-white">
                  <button
                    onClick={onClose}
                    className="w-full px-6 py-3.5 bg-gradient-to-r from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-700 text-white rounded-xl transition-all font-semibold shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                  >
                    <Check className="w-5 h-5" />
                    I Understand
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
